<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// TalkRobin: how to add TalkRobin to the home screen when the browser has no
// install prompt: iPhone/iPad (Share menu) or Android browsers like Firefox
// (browser menu).
const props = defineProps({
  platform: {
    type: String,
    default: 'apple',
    validator: value => ['apple', 'android'].includes(value),
  },
});

const { t } = useI18n();
const dialog = ref(null);

const STEPS = {
  apple: [
    { key: 'SHARE', icon: 'i-lucide-share' },
    { key: 'ADD', icon: 'i-lucide-square-plus' },
    { key: 'CONFIRM', icon: 'i-lucide-check' },
  ],
  android: [
    { key: 'MENU', icon: 'i-lucide-ellipsis-vertical' },
    { key: 'ADD', icon: 'i-lucide-square-plus' },
    { key: 'CONFIRM', icon: 'i-lucide-check' },
  ],
};

const prefix = computed(() =>
  props.platform === 'android' ? 'INSTALL_APP.ANDROID' : 'INSTALL_APP.APPLE'
);
const steps = computed(() => STEPS[props.platform]);

defineExpose({
  open: () => dialog.value.open(),
});
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t(`${prefix}.TITLE`)"
    :description="t(`${prefix}.DESCRIPTION`)"
    :confirm-button-label="t('INSTALL_APP.APPLE.DONE')"
    :show-cancel-button="false"
    @confirm="dialog.close()"
  >
    <ol class="flex flex-col gap-3 p-0 m-0 list-none">
      <li
        v-for="(step, index) in steps"
        :key="step.key"
        class="flex items-center gap-3 text-sm text-n-slate-12"
      >
        <span
          class="grid text-xs font-semibold rounded-full shrink-0 size-6 place-items-center bg-n-alpha-2 text-n-slate-11"
        >
          {{ index + 1 }}
        </span>
        <span class="flex-1">{{ t(`${prefix}.STEPS.${step.key}`) }}</span>
        <Icon :icon="step.icon" class="shrink-0 size-4 text-n-slate-11" />
      </li>
    </ol>
    <p class="mt-4 mb-0 text-xs text-n-slate-11">
      {{ t('INSTALL_APP.APPLE.NOTIFICATIONS') }}
    </p>
  </Dialog>
</template>
